import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiRequest } from "@/lib/api";
import { formatDate, getCategoryColor } from "@/lib/utils";
import { Edit, MoreVertical, Heart, MessageCircle, Eye, EyeOff, Star } from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent, 
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

interface JournalEntryProps {
  entry: {
    id: string;
    title: string;
    content: string;
    date: string;
    tags: string[];
    athleteId: string;
    isFavorite?: boolean;
    isPrivate?: boolean;
  };
  onEdit?: (entry: any) => void;
}

const JournalEntry = ({ entry, onEdit }: JournalEntryProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingPrivacy, setIsUpdatingPrivacy] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  
  // Limit content display to 300 characters initially
  const [expanded, setExpanded] = useState(false);
  const shouldTruncate = entry.content.length > 300;
  const displayContent = shouldTruncate && !expanded 
    ? entry.content.slice(0, 300) + "..." 
    : entry.content;

  const handleDelete = async () => {
    if (confirm("Are you sure you want to delete this journal entry?")) {
      setIsDeleting(true);
      try {
        await apiRequest(`/api/journal/${entry.id}`, { method: 'DELETE' });
        toast({
          title: "Entry deleted",
          description: "Journal entry has been deleted successfully."
        });
        // Invalidate the journal entries query to refresh the list
        queryClient.invalidateQueries({ queryKey: ['/api/athletes', entry.athleteId, 'journal'] });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete journal entry.",
          variant: "destructive"
        });
      }
      setIsDeleting(false);
    }
  };

  const togglePrivacy = async () => {
    setIsUpdatingPrivacy(true);
    try {
      await apiRequest(`/api/journal/${entry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPrivate: !entry.isPrivate })
      });
      toast({
        title: "Privacy updated",
        description: `Entry is now ${!entry.isPrivate ? 'private' : 'public'}`
      });
      // Invalidate the journal entries query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['/api/athletes', entry.athleteId, 'journal'] });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update entry privacy.",
        variant: "destructive"
      });
    }
    setIsUpdatingPrivacy(false);
  };

  const toggleFavorite = async () => {
    setIsTogglingFavorite(true);
    try {
      await apiRequest(`/api/journal/${entry.id}/favorite`, { method: 'PATCH' });
      toast({
        title: entry.isFavorite ? "Removed from favorites" : "Added to favorites",
        description: entry.isFavorite ? 
          "This entry is no longer a favorite moment" : 
          "This entry is now marked as a favorite moment"
      });
      queryClient.invalidateQueries({ queryKey: [`/api/athletes/${entry.athleteId}/journal`] });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update favorite status",
        variant: "destructive"
      });
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  return (
    <Card className="p-5 border-b border-[#415A77]/40">
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">{entry.title}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFavorite}
              disabled={isTogglingFavorite}
              className="p-1 h-auto hover:bg-transparent"
            >
              <Star 
                className={`h-4 w-4 ${entry.isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-slate-400 hover:text-yellow-400'}`}
              />
            </Button>
            {entry.isPrivate ? (
              <EyeOff className="h-4 w-4 text-slate-400" />
            ) : (
              <Eye className="h-4 w-4 text-slate-400" />
            )}
          </div>
          <p className="text-slate-400 text-sm">{formatDate(entry.date)}</p>
        </div>
        <div className="flex space-x-2">
          {onEdit && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => onEdit(entry)} 
              className="text-slate-400 hover:text-slate-200"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-slate-400 hover:text-slate-200"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(entry)}>
                  <Edit className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                onClick={togglePrivacy}
                disabled={isUpdatingPrivacy}
              >
                {entry.isPrivate ? (
                  <>
                    <Eye className="mr-2 h-4 w-4" /> Make visible to family
                  </>
                ) : (
                  <>
                    <EyeOff className="mr-2 h-4 w-4" /> Make private
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigator.clipboard.writeText(entry.content)}>
                Copy content
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleDelete}
                disabled={isDeleting}
                className="text-destructive focus:text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-slate-200 whitespace-pre-line">{displayContent}</p>
        {shouldTruncate && (
          <Button 
            variant="link" 
            onClick={() => setExpanded(!expanded)} 
            className="p-0 h-auto mt-1 text-primary"
          >
            {expanded ? "Show less" : "Read more"}
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center flex-wrap gap-2">
          {entry.tags && entry.tags.map((tag, index) => (
            <Badge key={index} variant="outline" className={getCategoryColor(tag)}>
              {tag}
            </Badge>
          ))}
        </div>
        <div className="flex items-center text-slate-400 text-sm">
          <Button variant="ghost" size="sm" className="flex items-center hover:text-primary transition">
            <Heart className="h-4 w-4 mr-1" /> 3
          </Button>
          <Button variant="ghost" size="sm" className="flex items-center ml-3 hover:text-primary transition">
            <MessageCircle className="h-4 w-4 mr-1" /> 1
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default JournalEntry;
